import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { jsonFetch } from '@/lib/api/jsonFetch'
import type {
  Assignee,
  Category,
  CreateRequestBody,
  Priority,
  RegulatoryStage,
  RegulatoryStats,
  RequestDetail,
  RequestListResponse,
  RequestType,
  ResponseTemplate,
  StageRequestType,
  TemplatePreview,
  RequestSummary,
} from './types'

const KEY = ['regulatory'] as const
const STAGES_KEY = ['regulatory', 'stages'] as const
const TEMPLATES_KEY = ['regulatory', 'templates'] as const

export interface RequestListFilters {
  request_type?: RequestType
  stage_id?: string
  assigned_to?: Assignee | 'unassigned'
  priority?: Priority
  category?: Category
  is_archived?: boolean
  overdue_only?: boolean
  search?: string
  page?: number
  limit?: number
}

export function useRequestList(filters: RequestListFilters = {}) {
  const params = new URLSearchParams()
  if (filters.request_type) params.set('request_type', filters.request_type)
  if (filters.stage_id) params.set('stage_id', filters.stage_id)
  if (filters.assigned_to) params.set('assigned_to', filters.assigned_to)
  if (filters.priority) params.set('priority', filters.priority)
  if (filters.category) params.set('category', filters.category)
  if (filters.is_archived) params.set('is_archived', 'true')
  if (filters.overdue_only) params.set('overdue_only', 'true')
  if (filters.search) params.set('search', filters.search)
  params.set('page', String(filters.page ?? 1))
  params.set('limit', String(filters.limit ?? 200))
  const qs = params.toString()

  return useQuery({
    queryKey: [...KEY, 'list', filters],
    queryFn: () => jsonFetch<RequestListResponse>(`/api/regulatory/requests?${qs}`),
    staleTime: 15_000,
    refetchInterval: 60_000,
  })
}

export function useRegulatoryStats() {
  return useQuery({
    queryKey: [...KEY, 'stats'],
    queryFn: () => jsonFetch<RegulatoryStats>('/api/regulatory/requests/stats'),
    staleTime: 30_000,
  })
}

export function useRequestDetail(requestId: string | null) {
  return useQuery({
    queryKey: [...KEY, 'detail', requestId],
    queryFn: () => jsonFetch<RequestDetail>(`/api/regulatory/requests/${requestId}`),
    enabled: !!requestId,
    staleTime: 5_000,
  })
}

export function useStages(requestType?: RequestType) {
  const params = requestType ? `?request_type=${requestType}` : ''
  return useQuery({
    queryKey: [...STAGES_KEY, requestType ?? 'all'],
    queryFn: () =>
      jsonFetch<{ items: RegulatoryStage[] }>(`/api/regulatory/stages${params}`),
    staleTime: 60_000,
  })
}

export function useTemplates(requestType?: RequestType, category?: Category) {
  const params = new URLSearchParams()
  if (requestType) params.set('request_type', requestType)
  if (category) params.set('category', category)
  const qs = params.toString()
  return useQuery({
    queryKey: [...TEMPLATES_KEY, requestType ?? 'all', category ?? 'all'],
    queryFn: () =>
      jsonFetch<{ items: ResponseTemplate[] }>(
        `/api/regulatory/templates${qs ? `?${qs}` : ''}`,
      ),
    staleTime: 60_000,
  })
}

// ─── Mutations ──────────────────────────────────────────────────────

export function useCreateRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateRequestBody) =>
      jsonFetch<RequestDetail>('/api/regulatory/requests', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function usePatchRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; edits: Record<string, unknown> }) =>
      jsonFetch<RequestDetail>(`/api/regulatory/requests/${vars.id}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useMoveStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; targetStageId: string; note?: string }) =>
      jsonFetch(`/api/regulatory/requests/${vars.id}/move-stage`, {
        method: 'POST',
        body: { target_stage_id: vars.targetStageId, note: vars.note },
      }),
    onMutate: async (vars) => {
      await qc.cancelQueries({ queryKey: [...KEY, 'list'] })
      const previousData = qc.getQueriesData<RequestListResponse>({ queryKey: [...KEY, 'list'] })
      qc.setQueriesData<RequestListResponse>({ queryKey: [...KEY, 'list'] }, (old) => {
        if (!old?.items) return old
        return {
          ...old,
          items: old.items.map((item: RequestSummary) =>
            item.id === vars.id ? { ...item, stage_id: vars.targetStageId } : item,
          ),
        }
      })
      return { previousData }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          qc.setQueryData(queryKey, data)
        }
      }
    },
    onSettled: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useArchiveRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; reason?: string }) =>
      jsonFetch(`/api/regulatory/requests/${vars.id}/archive`, {
        method: 'POST',
        body: { reason: vars.reason },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useUnarchiveRequest() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch(`/api/regulatory/requests/${id}/unarchive`, { method: 'POST' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useSendResponse() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: {
      id: string
      target_email: string
      template_id?: string
      subject?: string
      body?: string
    }) =>
      jsonFetch(`/api/regulatory/requests/${vars.id}/send-response`, {
        method: 'POST',
        body: {
          target_email: vars.target_email,
          template_id: vars.template_id,
          subject: vars.subject,
          body: vars.body,
        },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function useReassignType() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; request_type: RequestType }) =>
      jsonFetch<RequestDetail>(
        `/api/regulatory/requests/${vars.id}/reassign-type`,
        { method: 'POST', body: { request_type: vars.request_type } },
      ),
    // KEY prefixes both the list and detail query keys, so a single
    // invalidate refreshes both boards + the open detail.
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  })
}

export function usePreviewTemplate() {
  return useMutation({
    mutationFn: (vars: { templateId: string; requestId: string }) =>
      jsonFetch<TemplatePreview>(
        `/api/regulatory/templates/${vars.templateId}/preview?request_id=${vars.requestId}`,
        { method: 'POST' },
      ),
  })
}

// ─── Stage management ───────────────────────────────────────────────

export function useReorderStages() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (order: { id: string; sequence: number }[]) =>
      jsonFetch<{ items: RegulatoryStage[] }>('/api/regulatory/stages/reorder', {
        method: 'POST',
        body: { order },
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAGES_KEY }),
  })
}

export function useCreateStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      name: string
      request_type: StageRequestType
      color?: string
      is_terminal?: boolean
      is_archive?: boolean
    }) =>
      jsonFetch<RegulatoryStage>('/api/regulatory/stages', {
        method: 'POST',
        body,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAGES_KEY }),
  })
}

export function usePatchStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; edits: Record<string, unknown> }) =>
      jsonFetch<RegulatoryStage>(`/api/regulatory/stages/${vars.id}`, {
        method: 'PATCH',
        body: vars.edits,
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: STAGES_KEY }),
  })
}

export function useDeleteStage() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (vars: { id: string; migrateToStageId?: string }) => {
      const qs = vars.migrateToStageId
        ? `?migrate_to_stage_id=${vars.migrateToStageId}`
        : ''
      return jsonFetch(`/api/regulatory/stages/${vars.id}${qs}`, {
        method: 'DELETE',
      })
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['regulatory'] }),
  })
}
